/**
 *  IRATTÁRI TERV EXCELBŐL.
 *
 *  Elsősorban a KRÉTA exportját olvassa (Exp_IrattariTervTetelListModel_*.xlsx):
 *    Tételszám és tárgy | Tételszám | Tárgy | Megőrzési mód | Csoport | Megőrzési határidő
 *  de az oszlopokat a fejléc NEVE alapján keresi, így a sorrend és a plusz oszlopok
 *  nem számítanak; saját táblázatból is betölthető, ha a fejléc hasonló.
 *
 *  A megőrzési mód leképezése a terv mezőire:
 *    „Örökös megőrzés"   → nem selejtezhető, megőrzési idő nincs
 *    „Levéltári átadás"  → levéltári és nem selejtezhető (az irat nem semmisül meg,
 *                          hanem a megőrzési idő leteltével levéltárba kerül)
 *    „Selejtezés"        → a megőrzési határidő („+5 év") adja a megőrzés éveit
 */

export interface TervTetel {
  tetelszam: string;
  megnevezes: string;
  csoport: string | null;
  megorzes_ev: number | null;
  nem_selejtezheto: 0 | 1;
  leveltari: 0 | 1;
}

export interface TervImportEredmeny {
  tetelek: TervTetel[];
  /** Kihagyott sorok emberi olvasásra (üres tételszám, ismétlődés). */
  kihagyott: string[];
  /** Ha a fejléc nem ismerhető fel, itt a magyarázat, és a tételek üresek. */
  hiba?: string;
}

const norm = (v: unknown) => String(v ?? '')
  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ').trim();

const szoveg = (v: unknown) => String(v ?? '').replace(/\s+/g, ' ').trim();

/** Egy lap sorai (tömbök tömbje, pl. XLSX sheet_to_json header:1) → terv-tételek. */
export function tervSorokbol(sorok: unknown[][]): TervImportEredmeny {
  const kihagyott: string[] = [];
  const fejIndex = sorok.findIndex((sor) => (sor || []).some((c) => norm(c) === 'tetelszam'));
  const osszevontFej = sorok.findIndex((sor) => (sor || []).some((c) => norm(c).startsWith('tetelszam es targy')));
  const fejSor = fejIndex >= 0 ? fejIndex : osszevontFej;
  if (fejSor < 0) {
    return { tetelek: [], kihagyott, hiba: 'Nem találtam „Tételszám” oszlopot a táblázat fejlécében.' };
  }

  const fej = (sorok[fejSor] || []).map(norm);
  const oszlop = (...felt: ((h: string) => boolean)[]) => {
    for (const f of felt) { const i = fej.findIndex(f); if (i >= 0) return i; }
    return -1;
  };
  const iSzam = oszlop((h) => h === 'tetelszam');
  const iTargy = oszlop((h) => h === 'targy', (h) => h === 'megnevezes', (h) => h.startsWith('targy'));
  const iOsszevont = oszlop((h) => h.startsWith('tetelszam es targy'));
  const iMod = oszlop((h) => h.includes('megorzesi mod'), (h) => h === 'megorzes modja');
  const iCsoport = oszlop((h) => h === 'csoport', (h) => h.includes('focsoport'));
  const iHatarido = oszlop((h) => h.includes('hatarido'), (h) => h.includes('megorzesi ido'), (h) => h === 'megorzes ev');

  const tetelek: TervTetel[] = [];
  const latott = new Set<string>();

  for (let r = fejSor + 1; r < sorok.length; r++) {
    const sor = sorok[r] || [];
    if (!sor.some((c) => szoveg(c))) continue;

    let tetelszam = iSzam >= 0 ? szoveg(sor[iSzam]) : '';
    let megnevezes = iTargy >= 0 ? szoveg(sor[iTargy]) : '';
    //  „1., TÁRGYA: Intézménylétesítés…" – ha a külön oszlopok hiányoznak.
    if ((!tetelszam || !megnevezes) && iOsszevont >= 0) {
      const m = szoveg(sor[iOsszevont]).match(/^\s*([^,]+?)\s*,\s*T[AÁ]RGYA\s*:\s*(.*)$/i);
      if (m) { tetelszam = tetelszam || m[1]; megnevezes = megnevezes || m[2]; }
    }
    megnevezes = megnevezes.replace(/[,;]\s*$/, '');

    if (!tetelszam) { kihagyott.push(`${r + 1}. sor: nincs tételszám`); continue; }
    const kulcs = tetelszam.toLowerCase();
    if (latott.has(kulcs)) { kihagyott.push(`${r + 1}. sor: a(z) ${tetelszam} tételszám már szerepelt`); continue; }
    latott.add(kulcs);

    const mod = norm(iMod >= 0 ? sor[iMod] : '');
    const hatarido = szoveg(iHatarido >= 0 ? sor[iHatarido] : '');
    const orokos = /orokos|nem selejtezheto/.test(`${mod} ${norm(hatarido)}`);
    const leveltari = mod.includes('leveltar');
    const evSzam = hatarido.match(/(\d+)/);

    tetelek.push({
      tetelszam: tetelszam.slice(0, 30),
      megnevezes: megnevezes.slice(0, 255),
      csoport: szoveg(iCsoport >= 0 ? sor[iCsoport] : '').slice(0, 150) || null,
      megorzes_ev: orokos || !evSzam ? null : Number(evSzam[1]),
      nem_selejtezheto: orokos || leveltari ? 1 : 0,
      leveltari: leveltari ? 1 : 0,
    });
  }

  if (!tetelek.length && !kihagyott.length) {
    return { tetelek, kihagyott, hiba: 'A fejléc alatt nincs egyetlen tétel sem.' };
  }
  return { tetelek, kihagyott };
}
